import React, { useMemo, useState } from 'react';
import { BackofficeLayout } from '../BackofficeLayout';
import {
  Empty,
  Icon,
  IconButton,
  PageHeading,
  Pills,
  Search,
  Table,
  Tabs,
  Text,
} from 'barte-design-system';
import { PencilSimple } from '@phosphor-icons/react';
import type { ColumnDefinition, SortState } from 'barte-design-system';
import { DrawerNovoBolsao } from './DrawerNovoBolsao';
import { DrawerDetalheBolsao } from './DrawerDetalheBolsao';
import {
  Bolsao as BolsaoRecord,
  BolsaoStatus,
  INITIAL_BOLSOES,
  formatBRL,
  formatPercent,
  gapDe,
  statusDe,
} from './bolsao-mock-data';
import './Bolsao.css';

// ─── Status → Pills ───────────────────────────────────────────────────────────

type PillsStateValue = 'success' | 'info' | 'warning' | 'error' | 'default' | 'accent' | 'alert';

export const STATUS_PILL: Record<BolsaoStatus, PillsStateValue> = {
  'Dentro do esperado': 'default',
  'Abaixo do esperado': 'error',
  'Teto atingido': 'info',
};

const STATUS_FILTERS: Array<'todos' | BolsaoStatus> = [
  'todos',
  'Dentro do esperado',
  'Abaixo do esperado',
  'Teto atingido',
];

/** Tabs que exibem contagem em badge — só as que pedem atenção. */
const STATUS_COM_BADGE: Array<'todos' | BolsaoStatus> = ['Abaixo do esperado', 'Teto atingido'];

// ─── Colunas ──────────────────────────────────────────────────────────────────

const COLUMNS: ColumnDefinition<BolsaoRecord>[] = [
  {
    key: 'payfac',
    title: 'Payfac',
    dataIndex: 'payfac',
    width: 240,
    sortable: true,
    render: (_value, record) => (
      <div className="bolsao-cell">
        <Text variant="label-sm">{record.payfac}</Text>
        <Text variant="body-xs" className="bolsao-cell-secondary">ID: {record.payfacId}</Text>
      </div>
    ),
  },
  {
    key: 'percentTpv',
    title: '% TPV',
    dataIndex: 'percentTpv',
    width: 90,
    sortable: true,
    render: (_value, record) => formatPercent(record.percentTpv),
  },
  {
    key: 'teto',
    title: 'Teto',
    dataIndex: 'teto',
    width: 130,
    sortable: true,
    render: (_value, record) =>
      record.teto === null
        ? <Text variant="body-xs" className="bolsao-cell-secondary">Sem teto</Text>
        : formatBRL(record.teto),
  },
  {
    key: 'esperado',
    title: 'Esperado',
    dataIndex: 'esperado',
    width: 130,
    sortable: true,
    render: (_value, record) => (record.esperado === null ? '—' : formatBRL(record.esperado)),
  },
  {
    key: 'adquirido',
    title: 'Adquirido',
    dataIndex: 'adquirido',
    width: 130,
    sortable: true,
    render: (_value, record) => (record.adquirido === null ? '—' : formatBRL(record.adquirido)),
  },
  {
    key: 'gap',
    title: 'Gap',
    dataIndex: 'esperado',
    width: 210,
    sortable: true,
    render: (_value, record) => {
      const gap = gapDe(record);
      const acimaDoTeto =
        record.teto !== null && record.adquirido !== null && record.adquirido > record.teto;
      const escala = record.teto ?? record.esperado ?? 0;
      const adq = record.adquirido ?? 0;
      const fillPct = escala > 0 ? Math.min(100, (adq / escala) * 100) : 0;
      const overflowPct =
        acimaDoTeto && escala > 0
          ? Math.min(30, Math.max(6, ((adq - (record.teto as number)) / escala) * 100))
          : 0;
      return (
        <div className="bolsao-gap-cell">
          <Text
            variant="body-xs"
            className={acimaDoTeto ? 'bolsao-gap-acima' : gap > 0 ? 'bolsao-gap' : undefined}
          >
            {acimaDoTeto
              ? `+${formatBRL(adq - (record.teto as number))}`
              : gap > 0
                ? formatBRL(gap)
                : '—'}
          </Text>
          <div className="bolsao-progress-wrap bolsao-progress-mini">
            <div className="bolsao-progress">
              <div
                className={`bolsao-progress-fill${gap > 0 ? ' is-abaixo' : ''}`}
                style={{ width: `${fillPct}%` }}
              />
            </div>
            {overflowPct > 0 && (
              <div className="bolsao-progress-overflow" style={{ width: `${overflowPct}%` }} />
            )}
          </div>
        </div>
      );
    },
  },
  {
    key: 'status',
    title: 'Status',
    dataIndex: 'id',
    width: 150,
    render: (_value, record) => (
      <Pills label={statusDe(record)} state={STATUS_PILL[statusDe(record)]} size="sm" />
    ),
  },
];

// ─── Ordenação ────────────────────────────────────────────────────────────────

function sortValue(b: BolsaoRecord, key: string): string | number {
  switch (key) {
    case 'payfac': return b.payfac.toLowerCase();
    case 'percentTpv': return b.percentTpv;
    case 'teto': return b.teto ?? -1;
    case 'esperado': return b.esperado ?? -1;
    case 'adquirido': return b.adquirido ?? -1;
    case 'gap': return gapDe(b);
    default: return 0;
  }
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function Bolsao() {
  const [bolsoes, setBolsoes] = useState<BolsaoRecord[]>(INITIAL_BOLSOES);
  const [novoOpen, setNovoOpen] = useState(false);
  const [detalheId, setDetalheId] = useState<string | null>(null);
  const [detalheEditMode, setDetalheEditMode] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'todos' | BolsaoStatus>('todos');
  const [page, setPage] = useState(1);
  // Ordenação padrão: maior gap primeiro (prioriza quem está mais descoberto)
  const [sortState, setSortState] = useState<SortState>({ key: 'gap', direction: 'desc' });

  const detalhe = useMemo(
    () => bolsoes.find((b) => b.id === detalheId) ?? null,
    [bolsoes, detalheId],
  );

  // Colunas + coluna de edição (ícone visível no hover da linha)
  const columns = useMemo((): ColumnDefinition<BolsaoRecord>[] => [
    ...COLUMNS,
    {
      key: 'editar',
      title: '',
      dataIndex: 'id',
      width: 56,
      render: (_value, record) => (
        <div
          className="bolsao-row-edit"
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            variant="outline"
            size="sm"
            aria-label={`Editar configuração de ${record.payfac}`}
            onClick={() => {
              setDetalheId(record.id);
              setDetalheEditMode(true);
            }}
          >
            <Icon icon={PencilSimple} size={16} weight="regular" />
          </IconButton>
        </div>
      ),
    },
  ], []);

  const countByStatus = useMemo(() => {
    const counts: Record<string, number> = { todos: bolsoes.length };
    for (const b of bolsoes) {
      const s = statusDe(b);
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [bolsoes]);

  const filteredData = useMemo(() => {
    const byStatus = statusFilter === 'todos'
      ? bolsoes
      : bolsoes.filter((b) => statusDe(b) === statusFilter);

    const q = searchQuery.trim().toLowerCase();
    const bySearch = q
      ? byStatus.filter((b) =>
          b.payfac.toLowerCase().includes(q) || b.payfacId.toLowerCase().includes(q))
      : byStatus;

    if (!sortState.direction) return bySearch;
    const dir = sortState.direction === 'asc' ? 1 : -1;
    return [...bySearch].sort((a, b) => {
      const va = sortValue(a, sortState.key);
      const vb = sortValue(b, sortState.key);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [bolsoes, statusFilter, searchQuery, sortState]);

  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const pagedData = useMemo(
    () => filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredData, page],
  );

  function handleStatusFilter(status: 'todos' | BolsaoStatus) {
    setStatusFilter(status);
    setPage(1);
  }

  function handleCreate(novo: BolsaoRecord) {
    setBolsoes((prev) => [novo, ...prev]);
  }

  function handleUpdateConfig(id: string, percentTpv: number, teto: number | null) {
    setBolsoes((prev) =>
      prev.map((b) =>
        b.id === id
          ? {
              ...b,
              percentTpv,
              teto,
              // Recalcula o montante esperado com a % vigente, sem reiniciar período
              esperado: b.esperado === null
                ? null
                : (() => {
                    const base = (b.esperado / b.percentTpv) * percentTpv;
                    return teto !== null ? Math.min(base, teto) : base;
                  })(),
              ultimaAlteracao: { usuario: 'Você', data: '26/08/2026' },
            }
          : b,
      ),
    );
  }

  const isEmpty = bolsoes.length === 0;

  return (
    <>
      <BackofficeLayout activeItem="bolsao">
        <main className="content">
          <div className="content-inner">
            <PageHeading
              title="Bolsão"
              primaryActionLabel="Novo bolsão"
              onPrimaryAction={() => setNovoOpen(true)}
            />

            {isEmpty ? (
              <div className="bolsao-empty">
                <Empty
                  type="empty"
                  description={'Nenhum bolsão configurado ainda.\nCrie o primeiro para começar o acompanhamento.'}
                  buttonLabel="Novo bolsão"
                  onRetry={() => setNovoOpen(true)}
                />
              </div>
            ) : (
              <>
                {/* ── Barra de filtros ── */}
                <div className="bolsao-filter-bar">
                  <div className="bolsao-filter-tabs">
                    <Tabs>
                      {STATUS_FILTERS.map((status) => (
                        <Tabs.Tab
                          key={status}
                          active={statusFilter === status}
                          badge={STATUS_COM_BADGE.includes(status) ? countByStatus[status] ?? 0 : undefined}
                          onClick={() => handleStatusFilter(status)}
                        >
                          {status === 'todos' ? 'Todos' : status}
                        </Tabs.Tab>
                      ))}
                    </Tabs>
                  </div>

                  <div className="bolsao-filter-actions">
                    <Search
                      expanded
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Buscar"
                    />
                  </div>
                </div>

                {/* ── Tabela ── */}
                <div className="bolsao-table">
                  <Table<BolsaoRecord>
                    data={pagedData}
                    columns={columns}
                    rowKey={(r) => r.id}
                    size="sm"
                    emptyText="Nenhum payfac encontrado."
                    sortState={sortState}
                    onSort={(key, direction) => setSortState({ key, direction })}
                    pagination={{
                      currentPage: page,
                      totalPages,
                      totalItems: filteredData.length,
                      onPageChange: setPage,
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </main>
      </BackofficeLayout>

      <DrawerNovoBolsao
        isOpen={novoOpen}
        onClose={() => setNovoOpen(false)}
        bolsoes={bolsoes}
        onCreate={handleCreate}
      />

      <DrawerDetalheBolsao
        bolsao={detalhe}
        isOpen={detalhe !== null}
        startInEdit={detalheEditMode}
        onClose={() => {
          setDetalheId(null);
          setDetalheEditMode(false);
        }}
        onUpdateConfig={handleUpdateConfig}
      />
    </>
  );
}
